/**
 * Thin fetch wrapper implementing every endpoint in Doc/03-dac-ta-api.md.
 *
 * This entirely replaces the prototype's `S` + `localStorage` state layer for anything
 * that used to be "the data" (households/plots/submissions/answers) — those now live on
 * the server, fetched/patched through here. `offline/db.js` + `offline/queue.js` are the
 * only place `answers` still touch client storage, as a resilience layer, not the source
 * of truth.
 *
 * Conventions from Doc/03 §0:
 *  - Base URL /api/v1, Bearer auth.
 *  - Error envelope: { error: { code, message, field, details } }.
 *  - List envelope: { data: [...], meta: { page, pageSize, total, totalPages } }.
 *  - A 401 is retried exactly once via /auth/refresh before giving up.
 */

const BASE_URL = '/api/v1';
const AUTH_STORAGE_KEY = 'farmer.auth';

export class ApiError extends Error {
  constructor(status, body) {
    const err = body?.error || {};
    super(err.message || `HTTP ${status}`);
    this.status = status;
    this.code = err.code || 'UNKNOWN';
    this.field = err.field;
    this.details = err.details;
    this.body = body;
    // The backend's ErrorFilter spreads extra fields as siblings of `error`, not nested
    // under it (see apps/api/src/common/filters/error.filter.ts) — e.g. POSSIBLE_DUPLICATE
    // (409) carries top-level `candidates`, AUDIT_BLOCKED (422) carries top-level
    // `auditFindings`. Surface both as convenience accessors so callers don't need to know
    // that shape.
    this.candidates = body?.candidates;
    this.auditFindings = body?.auditFindings;
    // CONFLICT khi tạo Hộ trùng CCCD mang theo `{ id, fullName }` của hộ đã có,
    // để màn hình tạo hộ mở thẳng được hồ sơ đó thay vì bắt người dùng đi tìm.
    this.existing = body?.existing;
  }
}

// ---------------------------------------------------------------- auth store
function loadAuth() {
  try {
    return JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
}
function saveAuth(auth) {
  if (!auth) localStorage.removeItem(AUTH_STORAGE_KEY);
  else localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
}
export function getSession() {
  return loadAuth();
}
export function getCurrentUser() {
  return loadAuth()?.user || null;
}
export function isLoggedIn() {
  return !!loadAuth()?.accessToken;
}
export function clearSession() {
  saveAuth(null);
}

// ------------------------------------------------------------------ request
let refreshPromise = null;

async function refreshAccessToken() {
  const auth = loadAuth();
  if (!auth?.refreshToken) throw new ApiError(401, { error: { code: 'UNAUTHORIZED', message: 'Chưa đăng nhập' } });
  if (!refreshPromise) {
    refreshPromise = fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: auth.refreshToken }),
    })
      .then(async (res) => {
        const body = await safeJson(res);
        if (!res.ok) throw new ApiError(res.status, body);
        // The backend ROTATES refresh tokens on every /auth/refresh call — it revokes the
        // one we just sent and issues a new one (apps/api/src/modules/auth/auth.service.ts
        // AuthService.refresh(): old jti marked revokedAt, new token pair returned). Doc/03
        // only shows `{ accessToken }` in the example response, but the real endpoint also
        // returns a new `refreshToken`; if we don't persist it, the *next* refresh attempt
        // fails because the old one is already revoked server-side.
        saveAuth({ ...auth, accessToken: body.accessToken, refreshToken: body.refreshToken || auth.refreshToken });
        return body.accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

async function safeJson(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * @param {string} path - e.g. '/households'
 * @param {{method?:string, body?:any, auth?:boolean, retry?:boolean, raw?:boolean}} [opts]
 */
export async function request(path, opts = {}) {
  const { method = 'GET', body, auth = true, retry = true, raw = false } = opts;
  const headers = { 'Content-Type': 'application/json' };
  if (auth) {
    const session = loadAuth();
    if (session?.accessToken) headers.Authorization = `Bearer ${session.accessToken}`;
  }
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && auth && retry) {
    try {
      await refreshAccessToken();
      return request(path, { ...opts, retry: false });
    } catch (err) {
      clearSession();
      throw err;
    }
  }

  if (raw) {
    if (!res.ok) throw new ApiError(res.status, await safeJson(res));
    return res; // caller wants the raw Response (e.g. file download)
  }

  const json = await safeJson(res);
  if (!res.ok) throw new ApiError(res.status, json);
  return json;
}

// ------------------------------------------------------------------- auth
export async function login(username, password) {
  const body = await request('/auth/login', { method: 'POST', body: { username, password }, auth: false });
  saveAuth(body);
  return body;
}
/**
 * Hồ sơ của chính mình, đọc tươi từ máy chủ, và ghi đè bản đang lưu tại máy.
 *
 * `user` trong localStorage được chụp lúc đăng nhập nên đứng yên mãi: quản trị đổi
 * vai trò hay địa bàn xong, giao diện vẫn dựng menu theo quyền cũ cho tới lần đăng
 * nhập kế tiếp. Người dùng chỉ phát hiện khi bấm vào một mục rồi nhận 403.
 */
export async function fetchMe() {
  const me = await request('/auth/me');
  const auth = loadAuth();
  if (auth) saveAuth({ ...auth, user: { ...auth.user, ...me } });
  return me;
}

/**
 * Tự đổi mật khẩu. Máy chủ thu hồi mọi phiên cũ rồi cấp lại cặp token cho chính
 * thiết bị này — phải lưu ngay, nếu không lần gọi API kế tiếp sẽ dùng token vừa bị
 * thu hồi và người dùng bị đăng xuất ngay sau khi đổi mật khẩu thành công.
 */
export async function changePassword(currentPassword, newPassword) {
  const tokens = await request('/auth/change-password', {
    method: 'POST',
    body: { currentPassword, newPassword },
  });
  const auth = loadAuth();
  if (auth && tokens?.accessToken) {
    saveAuth({ ...auth, accessToken: tokens.accessToken, refreshToken: tokens.refreshToken || auth.refreshToken });
  }
  return tokens;
}

export async function logout() {
  const refreshToken = loadAuth()?.refreshToken;
  try {
    // Backend's RefreshDto requires refreshToken in the body to know which token to
    // revoke (apps/api/src/modules/auth/dto/refresh.dto.ts) — Doc/03 doesn't spell this
    // out, inferred from the actual DTO.
    if (refreshToken) await request('/auth/logout', { method: 'POST', body: { refreshToken } });
  } finally {
    clearSession();
  }
}

// -------------------------------------------------------------- households
export const households = {
  list: (params = {}) => request(`/households${qs(params)}`),
  get: (id) => request(`/households/${id}`),
  create: (payload) => request('/households', { method: 'POST', body: payload }),
  update: (id, payload) => request(`/households/${id}`, { method: 'PATCH', body: payload }),
  remove: (id, reason) => request(`/households/${id}`, { method: 'DELETE', body: { reason } }),
  /** US A3 — nhật ký chỉnh sửa hồ sơ: ai sửa, khi nào, giá trị cũ/mới. */
  changes: (id) => request(`/households/${id}/changes`),
};

// -------------------------------------------------------------------- plots
export const plots = {
  get: (id) => request(`/plots/${id}`),
  create: (payload) => request('/plots', { method: 'POST', body: payload }),
  update: (id, payload) => request(`/plots/${id}`, { method: 'PATCH', body: payload }),
  /** Xoá mềm — chặn nếu còn phiếu nháp tham chiếu. */
  remove: (id, reason) => request(`/plots/${id}`, { method: 'DELETE', body: { reason } }),
  changes: (id) => request(`/plots/${id}/changes`),
  /** Ranh giới vẽ trên bản đồ (GeoJSON Polygon), `null` để xoá. Điều tra viên được gọi. */
  setBoundary: (id, boundaryGeojson) => request(`/plots/${id}/boundary`, { method: 'PUT', body: { boundaryGeojson } }),
};

// ---------------------------------------------------------------- middlemen
/** Đại lý / MM — master data thứ ba (Doc/09 lát 1). Ghi chỉ dành cho điều phối viên trở lên. */
export const middlemen = {
  list: (params = {}) => request(`/middlemen${qs(params)}`),
  /** Danh sách rút gọn {id, code, name, type} cho ô chọn MM — toàn đơn vị, không lọc địa bàn. */
  options: (params = {}) => request(`/middlemen/options${qs(params)}`),
  get: (id) => request(`/middlemen/${id}`),
  create: (payload) => request('/middlemen', { method: 'POST', body: payload }),
  update: (id, payload) => request(`/middlemen/${id}`, { method: 'PATCH', body: payload }),
  remove: (id, reason) => request(`/middlemen/${id}`, { method: 'DELETE', body: { reason } }),
  changes: (id) => request(`/middlemen/${id}/changes`),
  /** Mẫu Excel nhập danh mục (kèm sheet tra cứu loại MM và địa bàn của đơn vị). */
  async importTemplate(params = {}) {
    const res = await request(`/middlemen/import-template${qs(params)}`, { raw: true });
    return res.blob();
  },
  /** Các dòng đã đọc ở trình duyệt; `dryRun: true` chỉ kiểm. */
  importRows: (payload) => request('/middlemen/import', { method: 'POST', body: payload }),
};

// ---------------------------------------------------------- đề xuất sửa (Doc/03 3c)
/**
 * Điều tra viên báo Hộ / Vườn / MM sai so với thực địa — dữ liệu chủ chỉ đổi khi người
 * duyệt đồng ý (D1). Điều tra viên chỉ thấy đề xuất của chính mình; các vai trò khác
 * thấy trong đơn vị + địa bàn. Danh sách trả `{ data, page, pageSize, total }`.
 */
export const changeRequests = {
  list: (params = {}) => request(`/change-requests${qs(params)}`),
  get: (id) => request(`/change-requests/${id}`),
  /** `clientRequestId` cho hàng đợi offline: gửi lại không tạo trùng (Q4). */
  create: (payload) => request('/change-requests', { method: 'POST', body: payload }),
  withdraw: (id) => request(`/change-requests/${id}/withdraw`, { method: 'POST' }),
  /** ≥ supervisor: `{auditFields, areaFactor, drafts[], usingProposal[], submittedBefore[]}`. */
  impact: (id) => request(`/change-requests/${id}/impact`),
  /**
   * ≥ supervisor, cả gói (Q7). `{decision, note?, reopenSubmissionIds?, acknowledgeStale?}`.
   * 409 `STALE_CHANGE_REQUEST` mang `staleFields` ở `err.body`.
   */
  decide: (id, payload) => request(`/change-requests/${id}/decide`, { method: 'POST', body: payload }),
};

// --------------------------------------------- nhật ký thay đổi (Doc/03 mục 3d)
/** ≥ supervisor. Toàn đơn vị, máy chủ áp hai trục phạm vi. Danh sách trả `{ data, meta }`. */
export const masterDataChanges = {
  list: (params = {}) => request(`/master-data-changes${qs(params)}`),
  /** `{actors, approvers, fields}` có thật trong phạm vi — để dựng các ô lọc. */
  filters: (params = {}) => request(`/master-data-changes/filters${qs(params)}`),
  async exportCsv(params = {}) {
    const res = await request(`/master-data-changes/export${qs(params)}`, { raw: true });
    return { blob: await res.blob(), filename: filenameOf(res, 'nhat-ky-thay-doi.csv') };
  },
};

// --------------------------------------------------------------------- forms
export const forms = {
  active: (formKey) => request(`/forms/${formKey}/active`),
  versions: (formKey) => request(`/forms/${formKey}/versions`),
  createVersion: (formKey, payload) => request(`/forms/${formKey}/versions`, { method: 'POST', body: payload }),
  // Ghi đè nội dung một phiên bản tại chỗ — chỉ được khi phiên bản chưa có phiếu nào dùng.
  updateVersion: (versionId, payload) => request(`/forms/versions/${versionId}`, { method: 'PATCH', body: payload }),
  publish: (versionId) => request(`/forms/versions/${versionId}/publish`, { method: 'POST' }),
};

// ------------------------------------------------------- đơn vị điều tra (Doc/06)
/**
 * Nhiều đơn vị điều tra dùng chung một hệ thống: chung admin, chung bộ câu hỏi và
 * ngưỡng đối chiếu, nhưng danh sách điều tra viên/nông hộ/phiếu thì tách biệt.
 *
 * `list()` mở cho mọi vai trò nhưng **server chỉ trả về đơn vị của chính họ** —
 * không phải lọc ở đây. Đừng dùng kết quả này làm căn cứ phân quyền trên giao diện:
 * nó là để hiển thị tên, còn ranh giới thật do máy chủ áp (R7).
 */
export const instances = {
  list: () => request('/instances'),
  create: (payload) => request('/instances', { method: 'POST', body: payload }),
  update: (id, payload) => request(`/instances/${id}`, { method: 'PATCH', body: payload }),
  regions: (id) => request(`/instances/${id}/regions`),
  /** Thay THẾ toàn bộ danh sách địa bàn phụ trách, không phải thêm vào. */
  setRegions: (id, regionIds) =>
    request(`/instances/${id}/regions`, { method: 'PUT', body: { regionIds } }),
  summary: (id) => request(`/instances/${id}/summary`),
};

// ------------------------------------------------------------ tài khoản (admin)
export const users = {
  list: (params = {}) => request(`/users${qs(params)}`),
  get: (id) => request(`/users/${id}`),
  create: (payload) => request('/users', { method: 'POST', body: payload }),
  update: (id, payload) => request(`/users/${id}`, { method: 'PATCH', body: payload }),
  /** Đặt lại mật khẩu hộ người khác — server thu hồi mọi phiên đăng nhập của họ. */
  resetPassword: (id, password) =>
    request(`/users/${id}/reset-password`, { method: 'POST', body: { password } }),
};

// -------------------------------------------------------- luật đối chiếu A/B
export const auditRules = {
  /** Danh mục luật kèm trạng thái bật/tắt. Mọi vai trò đọc được — bảng cảnh báo
   *  realtime cần biết luật nào đang tắt thì mới khớp với kết quả server. */
  list: (formKey) => request(`/audit-rules${formKey ? `?formKey=${encodeURIComponent(formKey)}` : ''}`),
  /** `rules: [{ code, enabled?, titleText?, fixText? }]` — chỉ admin. Trả về danh mục
   *  đầy đủ sau khi lưu. Bỏ trường nào thì trường đó giữ nguyên giá trị đang lưu;
   *  `titleText`/`fixText` bằng chuỗi rỗng hoặc null là quay về text luật tự sinh. */
  update: (rules) => request('/audit-rules', { method: 'PATCH', body: { rules } }),
};

// -------------------------------------------------------------- submissions
export const submissions = {
  list: (params = {}) => request(`/submissions${qs(params)}`),
  create: (payload) => request('/submissions', { method: 'POST', body: payload }),
  get: (id) => request(`/submissions/${id}`),
  /**
   * `baseUpdatedAt` là mốc `updatedAt` client nhìn thấy lần cuối. Nó không chặn
   * ghi — máy chủ vẫn merge rồi trả `staleBase: true` kèm `answers` hiện tại nếu
   * có người khác vừa sửa phiếu này.
   */
  patchAnswers: (id, answers, removeKeys, baseUpdatedAt) =>
    request(`/submissions/${id}/answers`, {
      method: 'PATCH',
      body: { answers, removeKeys, baseUpdatedAt },
    }),
  auditPreview: (id) => request(`/submissions/${id}/audit-preview`, { method: 'POST' }),
  submit: (id, overrides = []) => request(`/submissions/${id}/submit`, { method: 'POST', body: { overrides } }),
  reopen: (id, reason) => request(`/submissions/${id}/reopen`, { method: 'POST', body: { reason } }),
  approve: (id, reason) => request(`/submissions/${id}/approve`, { method: 'POST', body: { reason } }),
  reject: (id, reason) => request(`/submissions/${id}/reject`, { method: 'POST', body: { reason } }),
  auditFindings: (id) => request(`/submissions/${id}/audit-findings`),
  remove: (id) => request(`/submissions/${id}`, { method: 'DELETE' }),
};

// ------------------------------------------------------------------- export
function filenameOf(res, fallback) {
  const disposition = res.headers.get('Content-Disposition') || '';
  const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  const plain = disposition.match(/filename="([^"]+)"/i);
  return utf8 ? decodeURIComponent(utf8[1]) : plain ? plain[1] : fallback;
}

export const exportApi = {
  async xlsform(formKey) {
    const res = await request(`/export/xlsform/${formKey}`, { raw: true });
    return res.blob();
  },
  /**
   * Trả thẳng file (CSV/XLSX) chứ không phải jobId: server sinh đồng bộ — `202 + jobId`
   * trong Doc/03 §6 là phương án tuỳ chọn cho tập rất lớn, chưa dùng tới.
   * Lấy luôn tên file từ Content-Disposition để người tải thấy đúng tên server đặt.
   */
  async submissions(payload) {
    const res = await request('/export/submissions', { method: 'POST', body: payload, raw: true });
    const filename = filenameOf(res, `export.${payload.format}`);
    // CSV là bảng phẳng nên nhóm lặp bị bỏ lại. Máy chủ nêu tên chúng ở header để
    // người tải biết mình vừa nhận một file thiếu phần nào — trước đây chúng biến
    // mất trong im lặng.
    const omitted = (res.headers.get('X-Export-Omitted-Repeats') || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    return { blob: await res.blob(), filename, omittedRepeats: omitted };
  },
  job: (jobId) => request(`/export/jobs/${jobId}`),
};

// -------------------------------------------------------------------- admin
export const admin = {
  users: {
    list: (params = {}) => request(`/users${qs(params)}`),
    create: (payload) => request('/users', { method: 'POST', body: payload }),
    update: (id, payload) => request(`/users/${id}`, { method: 'PATCH', body: payload }),
  },
  regions: {
    list: () => request('/regions'),
    create: (payload) => request('/regions', { method: 'POST', body: payload }),
    update: (id, payload) => request(`/regions/${id}`, { method: 'PATCH', body: payload }),
  },
  // Không có `auditConfig` ở đây. Ngưỡng đối chiếu (`AUDIT_REC`) là dữ liệu của
  // phiên bản bộ câu hỏi, sửa qua `forms.createVersion` / `forms.updateVersion`
  // (tab "Ngưỡng đối chiếu" trong Question Builder) — không phải một endpoint quản
  // trị riêng. `/admin/audit-config` trong Doc/03 chưa từng tồn tại phía máy chủ;
  // đoạn client gọi nó đã nằm đây từ đầu và luôn trả 404.
};

function qs(params) {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (!entries.length) return '';
  return '?' + new URLSearchParams(entries).toString();
}
